/**
 * Звʼязок поштомата з 1С — для екрана поштомата (`app_min1.html` + `app.js`).
 *
 * Джерело — метод 1С `getPostamatsOnlineStatus` через наш бекенд:
 *   GET /webapp/locker/online-status?branchIDRef=…[&fresh=1] → StatusView
 *   { branchID, branchIDRef, supported, online: true|false|null, lastSeen,
 *     offlineSince, checkedAt, stale, refreshed, postamatType }
 * Бекенд сам тримає кеш і TTL, а `fresh=1` обходить TTL не частіше, ніж раз на
 * 5 с на поштомат (спільно для всіх курʼєрів), тож опитування звідси 1С не
 * перевантажує. `refreshed: true` — САМЕ цей запит сходив у 1С (а не віддав
 * кеш); `stale: true` — 1С не відповіла, дані старі. Власний збій бекенд
 * віддає як `supported:true, online:null, stale:true` — це «нічого не знаємо»,
 * як і обірваний запит.
 *
 * Що тут:
 *  • чисті помічники — розклад опитування після помилки 25, «N хв тому»,
 *    київський час, тексти плашок, розбір відповіді бекенда;
 *  • контролер опитування `createOnlinePoller` — один на екран: новий старт
 *    зупиняє попередній, відповідь зупиненого опитування відкидається.
 *    Таймери, годинник і мережа передаються ззовні — так його тестують на
 *    фейкових таймерах без DOM і без мережі.
 *
 * Листковий модуль, як `locker-cells.js`: жодних імпортів (ні ui.js, ні
 * api.js), щоб його так само міг підключити й courier.html.
 */

// ─── Розклад опитування після помилки 25 ────────────────────────────────────

/**
 * Помилка 25 — поштомат не відкрив комірку, бо зник звʼязок. Після неї
 * перевіряємо звʼязок одразу, далі кожні 10 с до першої хвилини, кожні 20 с до
 * другої і щохвилини до 15-ї (рішення власника 16.10). Курʼєр стоїть біля
 * поштомата: перші хвилини — найцінніші, далі частіше питати немає сенсу.
 * `untilMs` — межа фази від початку опитування, `everyMs` — крок у ній.
 */
export const ONLINE_POLL_SCHEDULE = Object.freeze([
  Object.freeze({ untilMs: 60_000, everyMs: 10_000 }),
  Object.freeze({ untilMs: 120_000, everyMs: 20_000 }),
  Object.freeze({ untilMs: 900_000, everyMs: 60_000 }),
]);

/**
 * Найменша пауза між запитами. Якщо попередній запит відповідав довго і
 * проскочив момент наступної перевірки, не стріляємо одразу ж удруге.
 */
export const ONLINE_POLL_MIN_GAP_MS = 2_000;

/** Фронтовий таймаут одного запиту: поодинокий виклик 1С швидкий. */
export const ONLINE_STATUS_TIMEOUT_MS = 8_000;

/** Скільки хвилин триває опитування після 25 — для текстів курʼєру. */
export function pollWindowMinutes(schedule = ONLINE_POLL_SCHEDULE) {
  const last = Array.isArray(schedule) && schedule.length ? schedule[schedule.length - 1] : null;
  return last ? Math.round(Number(last.untilMs) / 60_000) : 0;
}

/**
 * Моменти перевірок від початку опитування, мс: [0, 10 000, …, 900 000].
 * Фаза з нульовим або відʼємним кроком пропускається (інакше нескінченний цикл).
 */
export function pollOffsets(schedule = ONLINE_POLL_SCHEDULE) {
  const out = [0];
  let t = 0;
  for (const phase of Array.isArray(schedule) ? schedule : []) {
    const every = Number(phase && phase.everyMs);
    const until = Number(phase && phase.untilMs);
    if (!(every > 0) || !Number.isFinite(until)) continue;
    while (t + every <= until) {
      t += every;
      out.push(t);
    }
  }
  return out;
}

/**
 * Через скільки мс наступна перевірка, якщо від початку минуло `elapsedMs`.
 * `null` — розклад вичерпано, опитування закінчується.
 */
export function nextPollDelay(elapsedMs, { schedule = ONLINE_POLL_SCHEDULE, minGapMs = ONLINE_POLL_MIN_GAP_MS } = {}) {
  const elapsed = Number.isFinite(Number(elapsedMs)) ? Math.max(0, Number(elapsedMs)) : 0;
  const next = pollOffsets(schedule).find((offset) => offset > elapsed);
  if (next === undefined) return null;
  return Math.max(next - elapsed, Math.max(0, Number(minGapMs) || 0));
}

// ─── Час ────────────────────────────────────────────────────────────────────

/**
 * ISO-рядок від бекенда/1С → мс або null.
 * 1С віддає мікросекунди («…:04.391760Z») — частина рушіїв WebView їх не
 * розбирає, тому зрізаємо до мілісекунд. Рядок без зони — це UTC (контракт).
 */
export function parseIsoMs(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  let s = String(value).trim();
  if (!s) return null;
  s = s.replace(/(\.\d{3})\d+/, '$1');
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(s)) s += 'Z';
  const ms = Date.parse(s);
  return Number.isFinite(ms) ? ms : null;
}

/** Остання неділя місяця month (0–11) року y о 01:00 UTC — межа літнього часу ЄС. */
function lastSundayUtc(y, month) {
  const d = new Date(Date.UTC(y, month + 1, 0, 1));
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return d.getTime();
}

/**
 * Київський час без Intl: зсунутий Date, читати через getUTC*.
 * Intl із 'Europe/Kyiv' частина Android WebView не знає (RangeError), а
 * годинник телефона курʼєра може стояти будь-як — рахуємо самі (UTC+2, влітку
 * UTC+3 за правилом ЄС), як start-day.js.
 */
function kyivShifted(ms) {
  const y = new Date(ms).getUTCFullYear();
  const summer = ms >= lastSundayUtc(y, 2) && ms < lastSundayUtc(y, 9);
  return new Date(ms + (summer ? 3 : 2) * 3_600_000);
}

const pad2 = (n) => String(n).padStart(2, '0');

/** «HH:MM» за Києвом. */
export function kyivClock(ms) {
  if (!Number.isFinite(ms)) return '';
  const d = kyivShifted(ms);
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

/** «DD.MM» за Києвом. */
export function kyivDate(ms) {
  if (!Number.isFinite(ms)) return '';
  const d = kyivShifted(ms);
  return `${pad2(d.getUTCDate())}.${pad2(d.getUTCMonth() + 1)}`;
}

function kyivDayKey(ms) {
  const d = kyivShifted(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/**
 * «щойно» / «12 хв тому» / «3 год 20 хв тому» / «2 дн тому».
 * Майбутній момент (годинник телефона відстає) — «щойно», а не відʼємні хвилини.
 */
export function formatAgo(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return '';
  const diff = Math.max(0, now - ms);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'щойно';
  if (min < 60) return `${min} хв тому`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return m ? `${h} год ${m} хв тому` : `${h} год тому`;
  return `${Math.floor(h / 24)} дн тому`;
}

/**
 * Коли поштомат востаннє виходив на звʼязок: «14:59, 12 хв тому»; якщо це було
 * не сьогодні (за Києвом) — з датою: «13.10 13:43, 3 дн тому». null — невідомо.
 */
export function formatLastSignal(iso, now = Date.now()) {
  const clock = formatSignalClock(iso, now);
  return clock === null ? null : `${clock}, ${formatAgo(parseIsoMs(iso), now)}`;
}

/**
 * Лише момент сигналу, без «N хв тому»: «14:59», не сьогодні — «13.10 13:43».
 * Для короткого живого рядка в плашці 25 («Немає звʼязку з 14:59 · …»), який
 * має влазити в один рядок на телефоні. null — невідомо.
 */
export function formatSignalClock(iso, now = Date.now()) {
  const ms = parseIsoMs(iso);
  if (ms === null) return null;
  return kyivDayKey(ms) === kyivDayKey(now) ? kyivClock(ms) : `${kyivDate(ms)} ${kyivClock(ms)}`;
}

// ─── Відповідь бекенда ──────────────────────────────────────────────────────

const strOrNull = (v) => (v === null || v === undefined || v === '' ? null : String(v));
const isoOrNull = (v) => (parseIsoMs(v) === null ? null : String(v));

/**
 * StatusView бекенда → стан, з яким працює екран. Захисний розбір: будь-яке
 * сміття дає «нічого не знаємо» (`supported:false, online:null`), а не виняток.
 * Для непідтримуваного поштомата (не наш, не АПТ) `online` — завжди null.
 */
export function normalizeOnlineStatus(payload) {
  const p = payload && typeof payload === 'object' ? payload : {};
  const supported = p.supported === true;
  let online = null;
  if (supported && p.online === true) online = true;
  else if (supported && p.online === false) online = false;
  return {
    branchID: strOrNull(p.branchID),
    branchIDRef: strOrNull(p.branchIDRef),
    supported,
    online,
    lastSeen: isoOrNull(p.lastSeen),
    offlineSince: isoOrNull(p.offlineSince),
    checkedAt: isoOrNull(p.checkedAt),
    stale: p.stale === true,
    // Старий бекенд поля не віддає — тоді «не звіряли», а не «звірили».
    refreshed: p.refreshed === true,
    postamatType: strOrNull(p.postamatType),
  };
}

/**
 * Чи взагалі питати бекенд про звʼязок цієї точки. Метод 1С знає лише наші
 * поштомати (мережа 201); для відомих чужих мереж (Епіцентр, Rozetka) бекенд
 * однаково відповість `supported:false` — запит заощаджуємо. Невідома мережа
 * або її відсутність у відповіді by-qr — питаємо: вирішує бекенд.
 */
export function onlineCheckApplies(networkId) {
  const id = String(networkId || '').toLowerCase();
  return id !== 'epicentr' && id !== 'rozetka';
}

/** Чи показувати плашку «не на звʼязку»: лише явне `online === false`. */
export function isKnownOffline(status) {
  return Boolean(status && status.supported && status.online === false);
}

/**
 * «На звʼязку», якому після помилки 25 можна вірити: відповідь САМЕ цього
 * запиту з 1С (`refreshed`) і не застаріла. «На звʼязку» з кешу бекенда чи
 * зі старого знімка 25 щойно спростувала — на ньому опитування не зупиняємо.
 */
export function isConfirmedOnline(status) {
  return Boolean(status && status.supported === true && status.online === true
    && status.refreshed === true && status.stale !== true);
}

/**
 * Відповідь без жодного знання: бекенд не дістався 1С і кешу не має
 * (`online:null, stale:true`). Це те саме, що обірваний запит, — відомий
 * раніше стан вона не перекриває.
 */
export function isBlindStatus(status) {
  return Boolean(status && status.stale === true && status.online === null);
}

/**
 * «Не наш поштомат» остаточно — лише зі свіжої відповіді. `supported:false`
 * зі `stale:true` — збій, а не вирок: опитування після 25 не зупиняє і не
 * забороняє.
 */
export function isFinalUnsupported(status) {
  return Boolean(status && status.supported === false && status.stale !== true);
}

// ─── Тексти ─────────────────────────────────────────────────────────────────

/** Коли востаннє чули поштомат: lastSeen, а без нього — початок офлайну. */
function lastSignalLabel(status, now) {
  if (!status) return null;
  return formatLastSignal(status.lastSeen, now) || formatLastSignal(status.offlineSince, now);
}

/**
 * Плашка в шапці поштомата: «Поштомат не на звʼязку · останній сигнал 14:59,
 * 12 хв тому». null — показувати нічого (на звʼязку, невідомо, не наш).
 */
export function offlineBannerText(status, now = Date.now()) {
  if (!isKnownOffline(status)) return null;
  const signal = lastSignalLabel(status, now);
  return `Поштомат не на звʼязку · останній сигнал ${signal || 'невідомий'}`;
}

/**
 * Живий рядок у плашці помилки 25 — один рядок із точкою-індикатором під
 * короткою інструкцією, тому тексти стислі (≈45 знаків на 390 px): причина
 * («немає звʼязку з HH:MM») — тут, а не в тексті плашки; червона плашка в
 * шапці, поки є цей рядок, схована (app.js), щоб не дублювати.
 * phase: 'checking' — запит ще летить; 'polling' — перевіряємо за розкладом;
 * 'timeout' — розклад вичерпано, звʼязок так і не відновився; 'stopped' —
 * перевірку зупинено ззовні (нове сканування, вихід із поштомата): обіцянка
 * «перевіряємо далі» вже не діє; 'unavailable' — перевіряти нема як (демо,
 * чужа мережа, метод 1С поштомат не знає): лише причина помилки 25.
 * Повертає { tone: 'checking'|'offline'|'online'|'unknown'|'stopped', text }
 * і на 'timeout' ще cta: 'problem' — чекати далі марно: замість довгого
 * «натисніть «Повідомити проблему»» у рядку плашка виділяє саму кнопку.
 */
export function liveStatusLine({ status = null, phase = 'polling', now = Date.now(), windowMin = pollWindowMinutes() } = {}) {
  if (phase === 'stopped') {
    return { tone: 'stopped', text: 'Перевірку звʼязку зупинено' };
  }
  if (phase === 'unavailable') {
    return { tone: 'offline', text: 'Немає звʼязку з поштоматом' };
  }
  // «На звʼязку» зі знімка ДО помилки 25 чи з кешу бекенда сама 25 щойно
  // спростувала — поки 1С не підтвердить свіжою відповіддю, кажемо
  // «перевіряємо» / «невідомо», а не «на звʼязку».
  if (isConfirmedOnline(status) && phase !== 'checking') {
    return { tone: 'online', text: 'Поштомат на звʼязку' };
  }
  const offline = isKnownOffline(status);
  const since = offline ? lastSignalClock(status, now) : null;
  if (phase === 'timeout') {
    return offline
      ? { tone: 'offline', text: `Звʼязок за ${windowMin} хв не відновився`, cta: 'problem' }
      : { tone: 'unknown', text: `За ${windowMin} хв звʼязок не підтвердився`, cta: 'problem' };
  }
  if (offline) {
    return { tone: 'offline', text: `Немає звʼязку${since ? ` з ${since}` : ''} · перевіряємо далі` };
  }
  if (phase === 'checking') {
    return { tone: 'checking', text: 'Перевіряємо звʼязок із поштоматом…' };
  }
  return { tone: 'unknown', text: 'Стан звʼязку невідомий · перевіряємо далі' };
}

/** Момент останнього сигналу для живого рядка: lastSeen, а без нього — початок офлайну. */
function lastSignalClock(status, now) {
  if (!status) return null;
  return formatSignalClock(status.lastSeen, now) || formatSignalClock(status.offlineSince, now);
}

/**
 * Плашка «поштомат знову на звʼязку» замість попередження 25 — так само
 * компактна: заголовок, один-два рядки, кнопки в ряд.
 * На вкладенні нагадуємо про бронь: 1С її не скасовує, і команда, що не дійшла,
 * може відкрити комірку сама, щойно поштомат вийде на звʼязок.
 */
export function backOnlineAlert(kind) {
  return {
    title: 'Поштомат знову на звʼязку',
    message: kind === 'delivery'
      ? 'Якщо якась комірка відкрилась сама — закрийте її порожньою, потім спробуйте ще раз.'
      : 'Спробуйте відкрити комірку ще раз.',
  };
}

// ─── Контролер опитування ───────────────────────────────────────────────────

/**
 * Опитування звʼязку за розкладом після помилки 25. ОДНЕ на екран:
 * `start` зупиняє попереднє, а відповідь, що прийшла після зупинки чи
 * перезапуску, відкидається.
 *
 * opts:
 *  • fetchStatus(ident, { fresh: true }) → Promise<нормалізований стан>;
 *  • onUpdate({ status, phase, error, polls, nextInMs, context, ident }) —
 *    проміжний стан (phase 'checking' одразу на старті, далі 'polling');
 *  • onEnd({ reason, status, polls, context, ident }) — кінець опитування:
 *    'online' — 1С свіжою відповіддю підтвердила звʼязок (isConfirmedOnline);
 *    'unsupported' — не наш поштомат (лише не застаріла відповідь);
 *    'timeout' — розклад вичерпано; інше — зупинили ззовні (вихід із
 *    поштомата, нове сканування, 'restart' — новий старт).
 *    «На звʼязку» з кешу чи застаріле, «не підтримується» зі stale і порожня
 *    відповідь збою бекенда опитування НЕ завершують — розклад іде далі.
 *  • schedule, minGapMs, now, setTimer, clearTimer — для тестів.
 */
export function createOnlinePoller({
  fetchStatus,
  onUpdate = () => {},
  onEnd = () => {},
  schedule = ONLINE_POLL_SCHEDULE,
  minGapMs = ONLINE_POLL_MIN_GAP_MS,
  now = () => Date.now(),
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (id) => clearTimeout(id),
} = {}) {
  let run = null;
  let seq = 0;

  const safe = (fn, arg) => {
    try { fn(arg); } catch (_) { /* помилка відмальовки не має зупиняти опитування */ }
  };

  /** Завершити запуск `r` (за замовчуванням — поточний); чужий запуск не чіпає. */
  function end(reason, r = run) {
    if (!r || run !== r) return false;
    run = null;
    if (r.timer !== null) clearTimer(r.timer);
    r.timer = null;
    safe(onEnd, { reason, status: r.last, polls: r.polls, context: r.context, ident: r.ident });
    return true;
  }

  async function tick(r) {
    r.timer = null;
    if (run !== r) return;
    r.polls += 1;
    let status = null;
    let error = null;
    try {
      status = await fetchStatus(r.ident, { fresh: true });
    } catch (e) {
      error = e;
    }
    // Поки чекали — вийшли з поштомата, відсканували інший або 25 прийшла знову.
    if (run !== r) return;
    // Порожня відповідь збою бекенда — як обірваний запит: відомий стан лишається.
    if (status && typeof status === 'object' && !isBlindStatus(status)) r.last = status;
    if (isFinalUnsupported(status)) { end('unsupported', r); return; }
    if (isConfirmedOnline(status)) { end('online', r); return; }
    const delay = nextPollDelay(now() - r.startedAt, { schedule, minGapMs });
    if (delay === null) { end('timeout', r); return; }
    safe(onUpdate, { status: r.last, phase: 'polling', error, polls: r.polls, nextInMs: delay, context: r.context, ident: r.ident });
    r.timer = setTimer(() => { tick(r); }, delay);
  }

  return {
    /**
     * Почати опитування. `initial` — уже відомий стан (знімок після сканування
     * QR), щоб плашка одразу показала останній сигнал. Повертає номер запуску
     * або false, якщо стартувати нема з чим.
     */
    start(ident, context = null, { initial = null } = {}) {
      if (!ident || typeof fetchStatus !== 'function') return false;
      const key = String(ident);
      const prev = run && run.ident === key ? run.last : null;
      end('restart');
      seq += 1;
      const r = { ident: key, context, startedAt: now(), last: initial || prev || null, polls: 0, timer: null, id: seq };
      run = r;
      safe(onUpdate, { status: r.last, phase: 'checking', error: null, polls: 0, nextInMs: 0, context, ident: key });
      tick(r);
      return r.id;
    },
    /** Зупинити (вихід із поштомата, нове сканування, комірка відкрилась). */
    stop(reason = 'stopped') {
      return end(reason);
    },
    isActive() {
      return run !== null;
    },
    /** Поточний запуск (для діагностики й тестів) або null. */
    current() {
      return run ? { id: run.id, ident: run.ident, context: run.context, last: run.last, polls: run.polls, startedAt: run.startedAt } : null;
    },
  };
}
